import { useCallback, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import DisclaimerNotice from './components/DisclaimerNotice';
import ExtraPages from './components/ExtraPages';
import FeedPage from './components/FeedPage';
import GalleryPage from './components/GalleryPage';
import HomePage from './components/HomePage';
import SubmitCase from './components/SubmitCase';
import { createCase, listCases, updateCase } from './services/casesService';
import { createFlag } from './services/flagsService';
import { createReport } from './services/reportsService';
import { recordPageView } from './services/siteViewsService';

const { FiBell, FiHeart, FiMenu, FiShield, FiX } = FiIcons;

const routes = {
  Home: '/',
  'Submit a Case': '/submit-case',
  'Community Feed': '/community-feed',
  'Photo Gallery': '/photo-gallery',
  Support: '/support',
  Guidelines: '/guidelines',
  'Case Detail': '/case-detail'
};

function AppNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = Object.entries(routes).filter(([label]) => label !== 'Case Detail');

  return (
    <header className="topbar">
      <button
        className="brand"
        onClick={() => navigate('/')}
        aria-label="Bully Free home"
      >
        <span className="brand-mark"><SafeIcon icon={FiShield} /></span>
        <span>
          <b>Bully Free</b>
          <small>by Prof Dr. Vik</small>
        </span>
      </button>
      <button
        className="menu-toggle"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label="Toggle navigation"
      >
        <SafeIcon icon={menuOpen ? FiX : FiMenu} />
      </button>
      <nav className={`main-nav ${menuOpen ? 'is-open' : ''}`}>
        {navItems.map(([label, path]) => (
          <button
            key={path}
            className={location.pathname === path ? 'nav-link active' : 'nav-link'}
            onClick={() => {
              navigate(path);
              setMenuOpen(false);
            }}
          >
            {label}
          </button>
        ))}
        <a
          className="nav-link"
          href="https://www.facebook.com/groups/bullyfreemalaysia"
          target="_blank"
          rel="noreferrer"
          onClick={() => setMenuOpen(false)}
        >
          Join Our Community
        </a>
      </nav>
      <div className="top-actions">
        <span className="icon-button" aria-label="Community safety">
          <SafeIcon icon={FiBell} />
        </span>
      </div>
    </header>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cases, setCases] = useState([]);
  const [flags, setFlags] = useState([]);
  const [notice, setNotice] = useState('');
  const [totalViews, setTotalViews] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadCases() {
      try {
        const savedCases = await listCases();
        if (active) setCases(savedCases);
      } catch {
        if (active) showNotice('Community records are temporarily unavailable.');
      }
    }

    loadCases();
    recordPageView()
      .then((count) => {
        if (active) setTotalViews(count);
      })
      .catch(() => {
        if (active) showNotice('The total view count could not be updated.');
      });

    return () => {
      active = false;
    };
  }, []);

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 3500);
  };

  const addCase = async (item) => {
    try {
      const saved = await createCase({
        ...item,
        status: 'Published',
        reports: 0,
        views: 0,
        date: new Date().toLocaleDateString()
      });
      setCases((current) => [saved, ...current]);
      navigate('/');
      showNotice('Case submitted and added to the community feed.');
    } catch {
      showNotice('Could not save this case. Please try again later.');
    }
  };

  const markReported = async (item) => {
    try {
      await createReport({
        case_id: item.id,
        member_id: '',
        member_name: '',
        status: 'Confirmed',
        points_earned: 0,
        screenshot: false
      });
      const updatedCase = await updateCase({
        ...item,
        reports: Number(item.reports || 0) + 1
      });
      setCases((current) =>
        current.map((entry) => (entry.id === item.id ? updatedCase : entry))
      );
      showNotice('Your report count was recorded.');
    } catch {
      showNotice('The report could not be saved. Please try again later.');
    }
  };

  const recordView = useCallback(async (id) => {
    const item = cases.find((entry) => entry.id === id);
    if (!item) return;

    try {
      const updatedCase = await updateCase({
        ...item,
        views: Number(item.views || 0) + 1
      });
      setCases((current) =>
        current.map((entry) => (entry.id === id ? updatedCase : entry))
      );
    } catch {
      showNotice('The view count could not be updated.');
    }
  }, [cases]);

  const flagCase = async (item) => {
    try {
      const saved = await createFlag({
        case_id: item.id,
        member_id: '',
        reason: 'Community flag',
        status: 'Pending Review'
      });
      setFlags((current) => [saved, ...current]);
      showNotice('Flag saved for review.');
    } catch {
      showNotice('Could not save the flag. Please try again later.');
    }
  };

  const extraProps = {
    cases,
    onNavigate: (page) => navigate(routes[page] || '/'),
    onView: recordView,
    visitKey: location.key
  };

  return (
    <div className="app-shell">
      <AppNavigation />
      <main className="page-wrap">
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                cases={cases}
                totalViews={totalViews}
                onNavigate={(page) => navigate(routes[page] || '/')}
              />
            }
          />
          <Route path="/submit-case" element={<SubmitCase onSubmit={addCase} />} />
          <Route
            path="/community-feed"
            element={
              <FeedPage
                cases={cases}
                onReport={markReported}
                onFlag={flagCase}
                notify={showNotice}
                onNavigate={(path) =>
                  navigate(`/case-detail?id=${encodeURIComponent(path)}`)
                }
              />
            }
          />
          <Route path="/photo-gallery" element={<GalleryPage />} />
          <Route path="/support" element={<ExtraPages page="Support" />} />
          <Route path="/guidelines" element={<ExtraPages page="Guidelines" />} />
          <Route path="/disclaimer" element={<ExtraPages page="Disclaimer" />} />
          <Route
            path="/case-detail"
            element={<ExtraPages page="Case Detail" {...extraProps} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="site-footer">
        <span><SafeIcon icon={FiHeart} />A community rooted in care.</span>
        <span>Report only. No commenting, threats, or retaliation.</span>
        <button
          className="footer-disclaimer-link"
          type="button"
          onClick={() => navigate('/disclaimer')}
        >
          Disclaimer
        </button>
      </footer>
      <DisclaimerNotice onReadMore={() => navigate('/disclaimer')} />
      {notice && <div className="toast" role="status">{notice}</div>}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;